import React, { useState, useEffect } from 'react'
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { Badge } from '../../components/ui/Badge'
import { Plus, Edit, Trash2, Users, DollarSign, TrendingUp, Settings } from 'lucide-react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { planManagementApi, type SubscriptionPlan } from '../../services/planManagementApi'
import { CreatePlanModal } from '../../components/admin/modals/CreatePlanModal'
import { EditPlanModal } from '../../components/admin/modals/EditPlanModal'
import { DeletePlanModal } from '../../components/admin/modals/DeletePlanModal'
import { formatCurrency } from '../../utils/formatting'

export function PlanManagement() {
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(null)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  
  const queryClient = useQueryClient()

  // Fetch all plans with statistics
  const { data: plans = [], isLoading, error } = useQuery({
    queryKey: ['admin', 'plans'],
    queryFn: () => planManagementApi.getAllPlans(true, true), // include_inactive=true, include_stats=true
  })

  // Fetch plan analytics
  const { data: analytics } = useQuery({
    queryKey: ['admin', 'plan-analytics'],
    queryFn: () => planManagementApi.getPlanAnalytics(),
  })

  const handleEditPlan = (plan: SubscriptionPlan) => {
    setSelectedPlan(plan)
    setShowEditModal(true)
  }

  const handleDeletePlan = (plan: SubscriptionPlan) => {
    setSelectedPlan(plan)
    setShowDeleteModal(true)
  }

  const handleModalClose = () => {
    setSelectedPlan(null)
    setShowCreateModal(false)
    setShowEditModal(false)
    setShowDeleteModal(false)
    // Refetch data
    queryClient.invalidateQueries({ queryKey: ['admin', 'plans'] })
    queryClient.invalidateQueries({ queryKey: ['admin', 'plan-analytics'] })
  }

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-32 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="text-center py-8">
          <p className="text-red-600">Error loading subscription plans</p>
          <Button onClick={() => queryClient.invalidateQueries({ queryKey: ['admin', 'plans'] })} className="mt-4">
            Retry
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Subscription Plans</h1>
          <p className="text-gray-600">Manage subscription plans and pricing tiers</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)} className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          Create Plan
        </Button>
      </div>

      {/* Analytics Overview */}
      {analytics && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Plans</p>
                  <p className="text-2xl font-bold text-gray-900">{analytics.overview.total_plans}</p>
                  <p className="text-xs text-gray-500">{analytics.overview.active_plans} active</p>
                </div>
                <div className="h-12 w-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Settings className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Monthly Revenue</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatCurrency(analytics.overview.total_revenue_monthly)}
                  </p>
                  <p className="text-xs text-gray-500">Active subscriptions</p>
                </div>
                <div className="h-12 w-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <DollarSign className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Yearly Revenue</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatCurrency(analytics.overview.total_revenue_yearly)}
                  </p>
                  <p className="text-xs text-gray-500">Projected annual</p>
                </div>
                <div className="h-12 w-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <TrendingUp className="h-6 w-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Plan Distribution</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {Object.keys(analytics.plan_distribution).length}
                  </p>
                  <p className="text-xs text-gray-500">Different plans in use</p>
                </div>
                <div className="h-12 w-12 bg-orange-100 rounded-lg flex items-center justify-center">
                  <Users className="h-6 w-6 text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Plans Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {plans.map((plan) => (
          <Card key={plan.id} className={`${plan.is_active ? 'border-gray-200' : 'border-gray-100 bg-gray-50'}`}>
            <CardHeader className="pb-3">
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="text-lg">{plan.display_name}</CardTitle>
                  <p className="text-sm text-gray-500 font-mono">{plan.name}</p>
                </div>
                <div className="flex items-center gap-2">
                  {plan.is_active ? (
                    <Badge variant="default" className="bg-green-100 text-green-800">Active</Badge>
                  ) : (
                    <Badge variant="secondary">Inactive</Badge>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Pricing */}
              <div className="text-center py-4 border-t border-b border-gray-100">
                <div className="text-3xl font-bold text-gray-900">
                  {plan.messages_limit === -1 ? '∞' : formatCurrency(plan.price_monthly)}
                  <span className="text-lg font-normal text-gray-500">/mo</span>
                </div>
                {plan.price_yearly && (
                  <p className="text-sm text-gray-500 mt-1">
                    {formatCurrency(plan.price_yearly)}/year
                  </p>
                )}
              </div>

              {/* Limits */}
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">AI Usage Budget</span>
                  <span className="font-medium">
                    {(plan as any).ai_budget_usd > 0 ? `$${(plan as any).ai_budget_usd}` : 'Unlimited'}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Cost Multiplier</span>
                  <span className="font-medium">
                    {(plan as any).cost_multiplier ?? 3}×
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Team Members</span>
                  <span className="font-medium">
                    {plan.team_members_limit === -1 ? 'Unlimited' : plan.team_members_limit}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Agents</span>
                  <span className="font-medium">
                    {plan.chatbots_limit === -1 ? 'Unlimited' : plan.chatbots_limit}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Teams</span>
                  <span className="font-medium">
                    {plan.teams_limit === -1 ? 'Unlimited' : plan.teams_limit}
                  </span>
                </div>
              </div>

              {/* Usage Stats */}
              {plan.usage_stats && (
                <div className="border-t border-gray-100 pt-3 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Users</span>
                    <span className="font-medium text-blue-600">{plan.usage_stats.total_users}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Organizations</span>
                    <span className="font-medium text-green-600">{plan.usage_stats.total_organizations}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Monthly Revenue</span>
                    <span className="font-medium text-purple-600">
                      {formatCurrency(plan.usage_stats.revenue_monthly)}
                    </span>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleEditPlan(plan)}
                  className="flex-1"
                >
                  <Edit className="h-3 w-3 mr-1" />
                  Edit
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDeletePlan(plan)}
                  className="text-red-600 border-red-200 hover:bg-red-50"
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Modals */}
      {showCreateModal && (
        <CreatePlanModal
          onClose={handleModalClose}
        />
      )}

      {showEditModal && selectedPlan && (
        <EditPlanModal
          plan={selectedPlan}
          onClose={handleModalClose}
        />
      )}

      {showDeleteModal && selectedPlan && (
        <DeletePlanModal
          plan={selectedPlan}
          onClose={handleModalClose}
        />
      )}
    </div>
  )
}
